/* nh-deal-cart.js | North Home light theme, stage 7 deal cards (2026-11-01).
   A deal card (sections/sp-deals, form[data-nh-deal]) adds EXACTLY the items the deal needs in ONE
   POST /cart/add.js (items[]) so the automatic discount can match, then follows the same path as the theme's
   own add to cart (product-form.js #processBatchAddToCart and the owner's buy box in sp-product-after):
   dispatch CartLinesUpdateEvent (the drawer re-renders from the returned sections, the cart icon count and
   nh-cart-feedback's bump/van run), resolve it with the fresh cart, then open the cart drawer.
   Without this module (old browser, JS off, import map missing) the form posts natively to /cart/add and
   lands on the cart page with the same items. Never touches prices or discounts: Shopify applies them. */
import { CartLinesUpdateEvent } from '@shopify/events';

/* success: the shared big tick (snippets/nh-tick-burst, CSS "Tick burst" in nh-cart.css) plays on the card/tile for
   ADDED_MS, and the drawer opens DRAWER_DELAY after the tick starts so the shopper sees it first. */
const ADDED_MS = 1600;
const DRAWER_DELAY = 700;

function live(form, text) {
  let region = form.closest('.sp-deals')?.querySelector('[data-nh-deal-live]') || document.getElementById('nh-deal-live');
  if (!region) {
    region = document.createElement('p');
    region.id = 'nh-deal-live';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }
  region.textContent = '';
  if (text) setTimeout(() => { region.textContent = text; }, 50);
}

function setState(form, state, msg) {
  const btn = form.querySelector('[data-nh-deal-btn]');
  const cta = form.querySelector('[data-nh-deal-cta]');
  const err = form.querySelector('[data-nh-deal-error]');
  form.dataset.state = state;
  if (btn) {
    /* busy: aria-disabled, not disabled, so keyboard focus stays on the card; addDeal ignores repeat presses */
    btn.setAttribute('aria-disabled', state === 'busy' ? 'true' : 'false');
    btn.setAttribute('aria-busy', state === 'busy' ? 'true' : 'false');
  }
  if (cta) cta.textContent = state === 'busy' ? 'Adding…' : state === 'added' ? 'Added' : cta.dataset.label;
  if (err) { err.textContent = msg || ''; err.hidden = !msg; }
}

function sectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids;
}

async function freshCart() {
  const cic = document.querySelector('cart-items-component');
  if (cic && typeof cic.fetchCartData === 'function') {
    try { return await cic.fetchCartData(); } catch (e) { /* fall back below */ }
  }
  const r = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!r.ok) throw new Error('cart ' + r.status);
  return r.json();
}

function openDrawer() {
  const td = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
  if (td && typeof td.open === 'function' && !td.isOpen) { try { td.open(); } catch (e) { /* page still works */ } }
  return !!td;
}

function shortError(resp) {
  const d = String((resp && (resp.description || resp.message)) || '').trim();
  if (/sold out|out of stock|only add|all \d+ |quantity/i.test(d)) return 'Sold out for now: this deal could not be added.';
  return 'Could not add this deal. Please try again.';
}

async function addDeal(form) {
  if (/^(busy|added|soldout)$/.test(form.dataset.state || '')) return;   /* double tap / double Enter / tap during the tick: one add only */
  const items = String(form.dataset.nhItems || '').split(',').map((p) => {
    const [id, q] = p.split(':');
    return { id: Number(id), quantity: Number(q) };
  }).filter((i) => i.id > 0 && i.quantity > 0);
  if (!items.length) return;

  setState(form, 'busy');
  live(form, 'Adding the deal to your cart');
  const ids = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
    promise: deferred.promise,
  }));

  let resp;
  try {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items, sections: ids.join(','), sections_url: window.location.pathname }),
    });
    resp = await r.json().catch(() => ({ status: r.status }));
    if (!r.ok && !resp.status) resp.status = r.status;
  } catch (e) {
    resp = { status: 0, message: 'network' };
  }

  let cart = null;
  try { cart = await freshCart(); } catch (e) { /* resolve without a cart below */ }
  const failed = !!resp.status;
  const total = items.reduce((s, i) => s + i.quantity, 0);
  if (cart) {
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'product-form-component', sourceId: 'nh-deal-card', itemCount: total,
                sections: failed ? undefined : resp.sections, didError: failed },
    });
  } else {
    deferred.reject(new Error('cart refresh failed'));
  }

  if (failed) {
    const m = shortError(resp);
    setState(form, 'error', m);
    live(form, m);
    return;
  }
  setState(form, 'added');
  const saving = cart && cart.total_discount > 0 ? ' Your cart saving is now ' + money(cart.total_discount) + '.' : '';
  live(form, 'Deal added to your cart.' + saving);
  clearTimeout(form.nhT);
  clearTimeout(form.nhD);
  form.nhD = setTimeout(() => { if (!openDrawer()) window.location.href = '/cart'; }, DRAWER_DELAY);
  form.nhT = setTimeout(() => { if (form.dataset.state === 'added') setState(form, 'idle'); }, ADDED_MS);
}

function money(cents) {
  const whole = cents % 100 === 0;
  return 'R' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
}

document.addEventListener('submit', (e) => {
  const form = e.target instanceof HTMLFormElement && e.target.matches('form[data-nh-deal]') ? e.target : null;
  if (!form) return;
  e.preventDefault();
  addDeal(form);
});
document.documentElement.classList.add('nh-deal-js');
